import React, { useCallback } from 'react';

import algoliasearch from 'algoliasearch';
import algoliaSearchInsights, { InsightsMethodMap } from 'search-insights';

import { environment } from 'helpers/env';

import { useAuth } from 'hooks/useAuth';

import { SearchSortBy } from './search/types';

export type AlgoliaIndex =
  | 'Stories'
  | 'Speakers'
  | 'Tags'
  | 'Genres'
  | 'ContentTypes'
  | 'LanguageIntensities';

export enum AlgoliaIndexEnum {
  Tags = 'Tags',
  Speakers = 'Speakers',
  Stories = 'Stories',
  Genres = 'Genres',
  LanguageIntensities = 'LanguageIntensities',
  ContentTypes = 'ContentTypes',
}

const API_KEY = process.env.REACT_APP_ALGOLIA_SEARCH_KEY;

if (!API_KEY) throw new Error('Algolia API key not present');

algoliaSearchInsights('init', {
  appId: 'WUJOYL3NZM',
  apiKey: API_KEY,
});

export declare type InsightsClient = <
  MethodName extends keyof InsightsMethodMap
>(
  insight: MethodName,
  ...args: InsightsMethodMap[MethodName]
) => () => void;

export const sendSearchInsightBuilder: InsightsClient = (
  insight,
  args,
) => () => {
  if (!args.userToken) {
    return;
  }

  algoliaSearchInsights(insight, {
    ...args,
    index: args.index + '_' + environment(),
  });
};

export const useAlgolia = () => {
  const { user } = useAuth();

  const indexName = React.useCallback(
    (
      index: AlgoliaIndex,
      sortBy: SearchSortBy = SearchSortBy.Relevance,
    ): string => {
      const baseName = `${index}_${environment()}`;

      if (sortBy === SearchSortBy.Relevance || index !== 'Stories')
        return baseName;

      return `${baseName}_sort_by_${sortBy}`;
    },
    [],
  );

  const client = React.useMemo(() => {
    return algoliasearch('WUJOYL3NZM', API_KEY);
  }, []);

  const storiesIndex = React.useMemo(() => {
    return client.initIndex(indexName('Stories'));
  }, [client, indexName]);

  const tagsIndex = React.useMemo(() => {
    return client.initIndex(indexName('Tags'));
  }, [client, indexName]);

  const sendObjectViewedInsight = useCallback(
    ({
      eventName,
      index,
      objectId,
    }: {
      eventName: string;
      index: AlgoliaIndex;
      objectId: string;
    }) =>
      sendSearchInsightBuilder('viewedObjectIDs', {
        userToken: user?.trackingId,
        index: index,
        eventName,
        objectIDs: [objectId],
      })(),
    [user?.trackingId],
  );

  const sendConvertedObjectIDsAfterSearch = useCallback(
    ({
      eventName,
      objectId,
      index,
      queryID,
      userToken,
    }: {
      eventName: string;
      objectId: string;
      index: AlgoliaIndex;
      queryID: string;
      userToken: string | undefined;
    }) =>
      sendSearchInsightBuilder('convertedObjectIDsAfterSearch', {
        eventName,
        index,
        queryID,
        userToken,
        objectIDs: [objectId],
      })(),

    [],
  );

  const sendObjectClickedInsight = useCallback(
    ({
      eventName,
      objectId,
      index,
      queryId,
      position,
    }: {
      eventName: string;
      objectId: string;
      index: AlgoliaIndex;
      queryId: string;
      position: number;
    }) =>
      sendSearchInsightBuilder('clickedObjectIDsAfterSearch', {
        eventName,
        index: index,
        queryID: queryId,
        positions: [position],
        objectIDs: [objectId],
        userToken: user?.trackingId,
      })(),
    [user?.trackingId],
  );

  return {
    client,
    indexName,
    tagsIndex,
    storiesIndex,
    sendObjectViewedInsight,
    sendConvertedObjectIDsAfterSearch,
    sendObjectClickedInsight,
  };
};
